import { inject, Injectable, Optional } from '@angular/core';
import {
  AdminConfirmSignUpCommand,
  AdminCreateUserCommand,
  AdminDeleteUserCommand,
  AdminGetUserCommand,
  AdminSetUserPasswordCommand,
  AdminUpdateUserAttributesCommand,
  AuthenticationResultType,
  AuthFlowType,
  ChallengeNameType,
  CognitoIdentityProviderClient,
  CreateGroupCommand,
  DeleteGroupCommand,
  InitiateAuthCommand,
  InitiateAuthResponse,
  ListGroupsCommand,
  ListUsersCommand,
  RespondToAuthChallengeCommand,
  RespondToAuthChallengeResponse,
  SignUpCommand,
  UpdateGroupCommand,
} from '@aws-sdk/client-cognito-identity-provider';
import { Router } from '@angular/router';
import { StorageService } from './storage.service';
import { environment } from 'src/environments/environment';
import { SignUp } from '../interfaces/auth/signup.interface';
import { ToastService } from './toast.service';
import { LoadingService } from './loading.service';
import { ErrorsService } from './errors.service';
import { UtilsService } from './utils.service';
import { DbService } from './db.service';
import { AuthService } from './auth.service';
import { Observable } from 'rxjs';
import {
  Attribute,
  User,
  UserCognito,
} from '../interfaces/users/user.interface';
import { v4 as uuidv4 } from 'uuid';
import { UserRoleService } from './user-role.service';

@Injectable({
  providedIn: 'root',
})
export class CognitoService {
  username: string = '';
  session: string = '';
  userAttributes: any = {};

  private router = inject(Router);
  private _storage = inject(StorageService);
  private _toast = inject(ToastService);
  private _loading = inject(LoadingService);
  private _errors = inject(ErrorsService);
  private _utils = inject(UtilsService);
  private _db = inject(DbService);
  private _auth = inject(AuthService);
  private _userRole?: UserRoleService;

  private cognitoClient: CognitoIdentityProviderClient;
  private userPoolId = environment.cognito.userPoolId;
  private clientId = environment.cognito.userPoolClientId;

  constructor(@Optional() userRoleService?: UserRoleService) {
    this._userRole = userRoleService;
    
    this.cognitoClient = new CognitoIdentityProviderClient({
      region: environment.cognito.region,
      credentials: environment.cognito.credentials,
    } as any);
  }

  authenticate = async (credential: { email: string; password: string }) => {
    const command = new InitiateAuthCommand({
      ClientId: this.clientId,
      AuthFlow: AuthFlowType.USER_PASSWORD_AUTH,
      AuthParameters: {
        USERNAME: credential.email,
        PASSWORD: credential.password,
      },
    });
    try {
      const initialAuthReponse: any = await this.cognitoClient.send(
        command as any,
      );
      console.log('Auth response:', initialAuthReponse);
      this.username = credential.email;
      this.session = initialAuthReponse.Session || '';
      const tokens = await this.handleCognitoChallenge(initialAuthReponse);
      if (tokens) {
        const user = await this.adminGetUser();
        const couch = await this._auth.loginToCouchDB(credential, user);
        return (couch as any).ok;
      } else {
        return false;
      }
    } catch (error: any) {
      return error;
    }
  };

  unauthenticate = async () => {
    if (this._userRole) {
      this._userRole.clearRole();
    }
    
    await this._storage.clear();
    this.router.navigate(['/login']);
  };

  SetNewPassword = async (payload: {
    username: string;
    session: string;
    newPassword: string;
    userAttributes: any;
  }) => {
    const command = new RespondToAuthChallengeCommand({
      ClientId: this.clientId,
      ChallengeName: ChallengeNameType.NEW_PASSWORD_REQUIRED,
      ChallengeResponses: {
        USERNAME: payload.username,
        NEW_PASSWORD: payload.newPassword,
        ...payload.userAttributes,
      },
      Session: payload.session,
    });

    try {
      const response: RespondToAuthChallengeResponse =
        await this.cognitoClient.send(command);
      this.handleCognitoChallenge(response);
    } catch (error) {
      console.error('Error during new password set:', error);
      throw error;
    }
  };

  storeTokens = async (
    authenticationResult: AuthenticationResultType,
  ): Promise<any[]> => {
    const { AccessToken, IdToken, RefreshToken } = authenticationResult;

    return await Promise.all([
      this._storage.set('accessToken', AccessToken),
      this._storage.set('idToken', IdToken),
      this._storage.set('refreshToken', RefreshToken),
      this._storage.set('username', this.username),
    ]);
  };

  signUp = async (payload: SignUp) => {
    const command = new SignUpCommand({
      ClientId: this.clientId,
      Username: payload.username,
      Password: payload.password,
      UserAttributes: payload.UserAttributes,
    });
    this._loading.presentLoading();
    try {
      const response = await this.cognitoClient.send(command);
      this.handleCognitoChallenge(response);
      this._loading.dismissLoading();
    } catch (error) {
      this._toast.presentErrorToast(this._errors.handleCognitoError(error));
      this._loading.dismissLoading();
      throw error;
    }
  };

  handleCognitoChallenge = async (
    response: InitiateAuthResponse | RespondToAuthChallengeResponse,
  ): Promise<any> => {
    const {
      ChallengeName,
      Session,
      ChallengeParameters,
      AuthenticationResult,
    } = response;

    if (AuthenticationResult) {
      const tokens = await this.storeTokens(AuthenticationResult);
      if (tokens.length > 0) return true;
    }

    switch (ChallengeName) {
      case 'NEW_PASSWORD_REQUIRED': {
        this.userAttributes = ChallengeParameters?.['userAttributes']
          ? JSON.parse(ChallengeParameters['userAttributes'])
          : {};
        if (this.username && this.session && this.userAttributes) {
          this.router.navigate(['/set-new-password'], {
            state: {
              session: Session,
              username: this.username,
              userAttributes: this.userAttributes,
            },
          });
        } else {
          console.error(
            'Missing required parameters for NEW_PASSWORD_REQUIRED challenge.',
          );
        }
        break;
      }
      default: {
        console.error('Unhandled challenge:', ChallengeName);
        throw new Error(`Unsupported challenge: ${ChallengeName}`);
      }
    }
  };

  adminGetUser = async () => {
    const username = await this._storage.get('username');
    const command = new AdminGetUserCommand({
      UserPoolId: this.userPoolId,
      Username: username,
    });
    try {
      const response: any = await this.cognitoClient.send(command);
      if (!response.Username) {
        throw new Error('Username not found.');
      }
      const data: Array<{}> = this._utils.mapToObject(response?.UserAttributes);
      return data;
    } catch (error) {
      this._toast.presentErrorToast(this._errors.handleCognitoError(error));
      this._loading.dismissLoading();
      throw error;
    }
  };

  listGroups = () => {
    const command = new ListGroupsCommand({
      UserPoolId: this.userPoolId,
    });
    return new Observable((observer) => {
      this.cognitoClient
        .send(command)
        .then((response) => {
          const modifiedGroups = (response.Groups || []).map((group) => ({
            ...group,
            name: group.GroupName,
            id: uuidv4()
          }));
          observer.next(modifiedGroups);
          observer.complete();
        })
        .catch((error) => {
          observer.error(`Failed to list groups: ${error.message}`);
        });
    });
  };

  createGroup = (data: {
    groupName: string;
    description: string;
    precedence: number;
  }) => {
    const command = new CreateGroupCommand({
      GroupName: data.groupName,
      UserPoolId: this.userPoolId,
      Description: data.description,
      Precedence: data.precedence,
    });
    return new Observable((observer) => {
      this.cognitoClient
        .send(command)
        .then((response) => {
          observer.next(response.Group);
          observer.complete();
        })
        .catch((error) => {
          observer.error(`Failed to create group: ${error.message}`);
        });
    });
  };

  updateGroup = (data: {
    groupName: string;
    description?: string;
    precedence?: number;
  }) => {
    const command = new UpdateGroupCommand({
      GroupName: data.groupName,
      UserPoolId: this.userPoolId,
      Description: data.description,
      Precedence: data.precedence,
    });
    return new Observable((observer) => {
      this.cognitoClient
        .send(command)
        .then((response) => {
          observer.next(response.Group);
          observer.complete();
        })
        .catch((error) => {
          observer.error(`Failed to update group: ${error.message}`);
        });
    });
  };

  deleteGroup = (groupName: string) => {
    const command = new DeleteGroupCommand({
      GroupName: groupName,
      UserPoolId: this.userPoolId,
    });
    return new Observable((observer) => {
      this.cognitoClient
        .send(command)
        .then((response) => {
          observer.next(true);
          observer.complete();
        })
        .catch((error) => {
          observer.error(`Failed to delete group: ${error.message}`);
        });
    });
  };

  listUsers = () => {
    const command = new ListUsersCommand({
      UserPoolId: this.userPoolId,
    });
    return new Observable((observer) => {
      this.cognitoClient
        .send(command)
        .then((response: any) => {
          console.log(response.Users)
          const users = this.transformedUserData(response.Users);
          observer.next(users);
          observer.complete();
        })
        .catch((error: any) => {
          observer.error(`Error fetching users: ${error}`);
        });
    });
  };

  transformedUserData = (data: UserCognito[] | undefined): User[] => {
    if (!data) {
      return [];
    }
    return data.map((user: UserCognito) => {
      const attributes = user.Attributes.reduce(
        (acc: any, { Name, Value }: Attribute) => {
          acc[Name] = Value;
          return acc;
        },
        {},
      );
      return {
        username: user.Username,
        email: attributes.email,
        emailVerified: attributes.email_verified === 'true',
        name: attributes.name,
        sub: attributes.sub,
        status: user.UserStatus,
        enabled: user.Enabled,
        userCreateDate: user.UserCreateDate,
        userLastModifiedDate: user.UserLastModifiedDate,
        role: attributes['custom:role'],
        client: attributes['custom:client'] || null,
      };
    });
  };

  tranformedPaylodToAttributes = (payload: any) => {
    return Object.entries(payload).map(([key, value]) => ({
      Name: key,
      Value: value,
    }));
  };

  createUser = (data: any) => {
    const command = new AdminCreateUserCommand({
      UserPoolId: environment.cognito.userPoolId,
      Username: data.username,
      TemporaryPassword: data.password,
      UserAttributes: [
        { Name: 'email', Value: data.email },
        { Name: 'name', Value: data.name },
        { Name: 'custom:role', Value: data.role },
        { Name: 'custom:client', Value: data.client },
      ],
      MessageAction: 'SUPPRESS',
    });

    const setPasswordCommand = new AdminSetUserPasswordCommand({
      UserPoolId: environment.cognito.userPoolId,
      Username: data.username,
      Password: data.password,
      Permanent: true
    });

    return new Observable((observer) => {
      this.cognitoClient
        .send(command)
        .then((response: any) => {
          this.cognitoClient
          .send(setPasswordCommand)
          .then(() => {
            observer.next(response);
            observer.complete();
          })
          .catch((setPasswordError: any) => {
            observer.error({ message: `Set password failed: ${setPasswordError.message}` });
          });
        })
        .catch((error: any) => {
          observer.error({ message: `Create user failed: ${error.message}` });
        });
    });
  };

  deleteUser = (username: string) => {
    const command = new AdminDeleteUserCommand({
      UserPoolId: environment.cognito.userPoolId,
      Username: username,
    });

    return new Observable((observer) => {
      this.cognitoClient
        .send(command)
        .then((response) => {
          observer.next(response);
          observer.complete();
        })
        .catch((error) => {
          observer.error({ message: `Delete failed: ${error.message}` });
        });
    });
  };

  updateUser = (username: string, data: any) => {
    data = this.tranformedPaylodToAttributes(data);
    const command = new AdminUpdateUserAttributesCommand({
      UserPoolId: environment.cognito.userPoolId,
      Username: username,
      UserAttributes: data,
    });
    return new Observable((observer) => {
      this.cognitoClient
        .send(command)
        .then((response: any) => {
          observer.next(response);
          observer.complete();
        })
        .catch((error: any) => {
          observer.error({ message: `Update failed: ${error.message}` });
        });
    });
  };

  getUserBySub = (sub: string | null) => {
    const command = new ListUsersCommand({
      UserPoolId: environment.cognito.userPoolId,
      Filter: `sub = "${sub}"`,
    });
    return new Observable((observer) => {
      this.cognitoClient
        .send(command)
        .then((response: any) => {
          const users = response.Users || [];
          const data = this.transformedUserData(users);
          observer.next(data[0]);
          observer.complete();
        })
        .catch((error: any) => {
          observer.error({
            message: `Failed to fetch user with sub ${sub}: ${error.message}`,
          });
        });
    });
  };
}
